import { useSyncExternalStore } from 'react';
import type { Folder, FolderRole, MessageSummary } from '@/api';

/**
 * Dragging message rows onto the folder tree (#168, doc 06 "Drag to folder").
 *
 * The drag and the drop live in different trees: the rows are `MessageList`
 * inside `MailView`, the targets are `FolderTree` rows that `AppShell` draws in
 * the sidebar (and `MailView` in the handheld drawer). Prop-drilling a move
 * verb through the shell would make the shell know about messages, so the drag
 * is a small external store instead: the list starts it, the tree reads it to
 * decide what may be dropped where, and `MailView` registers the one handler
 * that actually moves mail.
 *
 * The state lives here, never in a row, because a virtualised row can be
 * unmounted mid-drag (the list scrolls under the pointer) and would take its
 * state, and its `dragend`, with it.
 */

export interface MessageDrag {
  /** What is being dragged: the selection when the grabbed row is in it, else that row. */
  rows: readonly MessageSummary[];
  /** True when `rows` is the list's selection, so the drop ends selection mode. */
  fromSelection: boolean;
}

/** The custom type every row drag carries; nothing else in the app uses it. */
export const MESSAGE_DRAG_TYPE = 'application/x-ols-mail-rows';

/**
 * Folders mail is never filed into by hand. The job folders are driven by
 * ADR 0076's job table (a message there has a send or a return scheduled), so
 * a message moved in by hand is one the scheduler knows nothing about. The `v`
 * palette applies the same rule (`moveTargets.ts`).
 */
export const NOT_A_MOVE_TARGET: ReadonlySet<FolderRole> = new Set<FolderRole>(['scheduled', 'snoozed']);

/** A drop moves, or with Ctrl / ⌥ held copies (ADR 0108). */
export type DropMode = 'move' | 'copy';

type DropHandler = (drag: MessageDrag, folder: Folder, mode: DropMode) => void;

let current: MessageDrag | null = null;
let handler: DropHandler | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): MessageDrag | null {
  return current;
}

/**
 * Ends the drag however it ends. Wired to `document` as well as the grid: when
 * the source row has been unmounted, its `dragend` is never dispatched, and a
 * highlight that outlives the drag is a tree that still looks like a target.
 */
function endOnDocument(): void {
  endMessageDrag();
}

export function startMessageDrag(drag: MessageDrag): void {
  current = drag;
  document.addEventListener('dragend', endOnDocument, true);
  document.addEventListener('drop', endOnDocument);
  emit();
}

export function endMessageDrag(): void {
  document.removeEventListener('dragend', endOnDocument, true);
  document.removeEventListener('drop', endOnDocument);
  if (current === null) return;
  current = null;
  emit();
}

/** The live drag, or null. Re-renders only when a drag starts or ends. */
export function useMessageDrag(): MessageDrag | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** `MailView` owns the verb; it registers it while mounted. */
export function registerDropHandler(next: DropHandler): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = undefined;
  };
}

/**
 * The rows a drop on `folder` would actually move: those not already in it.
 * A folder id names a folder inside ONE account (ADR 0012), so "already in it"
 * is the same id in the same mailbox; another mailbox's Inbox has the same id.
 */
export function rowsToMove(drag: MessageDrag, folder: Folder): MessageSummary[] {
  return drag.rows.filter((row) => row.folderId !== folder.id || row.accountId !== folder.accountId);
}

/** True when some dragged row lives in another mailbox than `folder`. */
export function crossesMailboxes(drag: MessageDrag, folder: Folder): boolean {
  return drag.rows.some((row) => row.accountId !== folder.accountId);
}

/** The reason a cross-mailbox target is unavailable offline (ADR 0108). */
export const OFFLINE_TRANSFER_REASON = 'Moving mail to another mailbox needs a connection.';

/**
 * Why `folder` will not take this drag, or `undefined` when it will.
 *
 * - `\Noselect`: IMAP cannot store a message in a container.
 * - The job folders (see `NOT_A_MOVE_TARGET`).
 * - Every row is already there: a move to where it is is no move.
 * - Offline, a folder of another mailbox: a transfer downloads and re-uploads
 *   each message on the server and is never queued (ADR 0108).
 *
 * Another mailbox's folder is a target like any other (ADR 0108, amending the
 * refusal of ADR 0056 §4): the rows from elsewhere are transferred.
 */
export function dropRefusal(
  drag: MessageDrag,
  folder: Folder,
  { online = true }: { online?: boolean } = {},
): string | undefined {
  if (!folder.selectable) return 'This folder cannot hold mail.';
  if (NOT_A_MOVE_TARGET.has(folder.role)) return 'Mail is not moved here by hand.';
  if (rowsToMove(drag, folder).length === 0) return 'Already in this folder.';
  if (!online && crossesMailboxes(drag, folder)) return OFFLINE_TRANSFER_REASON;
  return undefined;
}

/** A drop on `folder`: hands the drag to `MailView`, then ends it. */
export function dropMessages(
  folder: Folder,
  { mode = 'move', online = true }: { mode?: DropMode; online?: boolean } = {},
): void {
  const drag = current;
  endMessageDrag();
  if (drag === null || dropRefusal(drag, folder, { online }) !== undefined) return;
  handler?.(drag, folder, mode);
}

/** For tests: forget any drag a failed test left behind. */
export function resetMessageDragForTests(): void {
  endMessageDrag();
  handler = undefined;
}
